import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AnyLevel } from '../../../data/schemas';
import { gamesManifest, isSoppelLevel, resolveSoppelEndpoints } from '../../../data/loadData';
import { playVoice, stopVoice, unlockAudio } from '../voice';
import { useProgressStore } from '../../../store/progress';
import { useSessionStore } from '../../../store/session';
import { useProfileStore } from '../../../store/profile';
import { settingsFor } from '../../../store/ageSettings';

/** start → play → finish → celebration → reward */
export type LevelPhase = 'start' | 'play' | 'finish' | 'celebration' | 'reward';

export interface LevelRunnerApi {
  phase: LevelPhase;
  idleHintVisible: boolean;
  startPlay: () => void;
  beginFinish: () => void;
  beginCelebration: () => void;
  completeLevel: () => void;
  dismissReward: () => void;
  pokeActivity: () => void;
  reset: () => void;
}

/** Seconds to wait before hint number `count` (0 = first): base, 2×, 3×, capped at 4×. */
export function hintDelaySec(baseSec: number, count: number): number {
  return baseSec * Math.min(4, 1 + Math.max(0, count));
}

/**
 * Loads level JSON context and runs start → play → finish → celebration → reward.
 */
export function useLevelRunner(level: AnyLevel | null): LevelRunnerApi {
  const [phase, setPhase] = useState<LevelPhase>('start');
  const [idleHintVisible, setIdleHintVisible] = useState(false);
  const lastActivityRef = useRef(Date.now());
  /** Spoken hints since the last tap: each repeat waits longer so the voice never nags. */
  const hintCountRef = useRef(0);
  const markCompleted = useProgressStore((s) => s.markCompleted);
  const recordSessionLevel = useSessionStore((s) => s.recordLevelComplete);
  const ageBand = useProfileStore((s) => s.profile?.ageBand ?? null);

  const pokeActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    hintCountRef.current = 0;
    setIdleHintVisible(false);
  }, []);

  const reset = useCallback(() => {
    stopVoice();
    setPhase('start');
    setIdleHintVisible(false);
    lastActivityRef.current = Date.now();
    hintCountRef.current = 0;
  }, []);

  useEffect(() => {
    reset();
  }, [level?.id, reset]);

  useEffect(() => {
    if (!level || phase !== 'start') return;
    unlockAudio();
    const line =
      isSoppelLevel(level) ? resolveSoppelEndpoints(level).start.voice : level.voice.intro;
    void playVoice(level.id, 'intro', line);
  }, [level, phase]);

  useEffect(() => {
    if (!level || phase !== 'finish') return;
    if (!isSoppelLevel(level)) return;
    const line = resolveSoppelEndpoints(level).finish.voice;
    void playVoice(level.id, 'hint', line);
  }, [level, phase]);

  useEffect(() => {
    if (!level || phase !== 'celebration') return;
    void playVoice(level.id, 'success', level.voice.success);
    const timer = window.setTimeout(() => setPhase('reward'), 2200);
    return () => window.clearTimeout(timer);
  }, [level, phase]);

  useEffect(() => {
    if ((phase !== 'play' && phase !== 'start' && phase !== 'finish') || !level) return;
    const idleSec = ageBand ? settingsFor(ageBand).idleHintSec : gamesManifest.ux.idleHintSec;
    const id = window.setInterval(() => {
      const idleFor = (Date.now() - lastActivityRef.current) / 1000;
      if (idleFor >= hintDelaySec(idleSec, hintCountRef.current)) {
        hintCountRef.current += 1;
        setIdleHintVisible(true);
        const hint =
          phase === 'start' && isSoppelLevel(level)
            ? resolveSoppelEndpoints(level).start.voice
            : phase === 'finish' && isSoppelLevel(level)
              ? resolveSoppelEndpoints(level).finish.voice
              : level.voice.hint;
        void playVoice(level.id, 'hint', hint);
        lastActivityRef.current = Date.now();
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [phase, level, ageBand]);

  const startPlay = useCallback(() => {
    unlockAudio();
    pokeActivity();
    setPhase('play');
  }, [pokeActivity]);

  const beginFinish = useCallback(() => {
    pokeActivity();
    setPhase('finish');
  }, [pokeActivity]);

  const beginCelebration = useCallback(() => {
    if (!level) return;
    pokeActivity();
    markCompleted(level.id, level.reward.sticker, level.reward.unlock);
    recordSessionLevel();
    setPhase('celebration');
  }, [level, markCompleted, recordSessionLevel, pokeActivity]);

  /** Alias used by games that jump straight to celebration (e.g. finish.action none). */
  const completeLevel = useCallback(() => {
    beginCelebration();
  }, [beginCelebration]);

  const dismissReward = useCallback(() => {
    stopVoice();
    setPhase('start');
  }, []);

  return useMemo(
    () => ({
      phase,
      idleHintVisible,
      startPlay,
      beginFinish,
      beginCelebration,
      completeLevel,
      dismissReward,
      pokeActivity,
      reset,
    }),
    [
      phase,
      idleHintVisible,
      startPlay,
      beginFinish,
      beginCelebration,
      completeLevel,
      dismissReward,
      pokeActivity,
      reset,
    ]
  );
}
